import * as React from 'react'
import { KvBox } from 'src/design-system/components/atoms/kvbox'
import { HeadcountOverview } from '../headcount-overview'
import styles from './CurrentImpact.module.scss'
import kvBoxStyles from 'src/design-system/components/atoms/kvbox/KvBox.module.scss'
import { useWfiContext } from '../WfiContext'
import { formatCurrency as $ } from '../utils'
import {
  Analysis,
  OrgSnapshot,
  PositionSkipped,
  Team,
  User,
  UserSkipped,
} from '../types'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { mean, sum } from 'lodash-es'
import { Warning } from '@phosphor-icons/react'
import { SkipWarning } from '../SkipWarning'

type CurrentImpactProps = {
  onSelect: (teamId: number) => void
  selectedTeam: number | null
  teams: Team[]
}

type SelectedSkip =
  | { positions: PositionSkipped[] }
  | { users: UserSkipped[] }
  | { inferredSalaries: User[] }

export const CurrentImpact: React.VFC<CurrentImpactProps> = ({
  onSelect,
  selectedTeam,
  teams,
}) => {
  const [selectedSkip, setSelectedSkip] = React.useState<SelectedSkip | null>(
    null
  )
  const { orgSnapshot } = useWfiContext()

  if (orgSnapshot == null) return null

  const teamSummary = selectedTeam
    ? getSummaryForTeam(orgSnapshot, selectedTeam)
    : null

  const summary = teamSummary ?? orgSnapshot
  const usersWithInferredSalaries = orgSnapshot.users.filter(
    (user) => user.salary_source === 'inferred'
  )
  const hasInferredSalaries = usersWithInferredSalaries.length > 0

  return (
    <>
      <div className={styles.container}>
        <div>
          <p className={styles.headcountTitle}>Team headcount overview</p>
          <HeadcountOverview onSelect={onSelect} teams={teams} />
        </div>
        <div className={styles.statsContainer}>
          <p className={styles.title}>Organizational Insights</p>
          <KvBox
            className={kvBoxStyles.bottomBorderOnly}
            Key={selectedTeam == null ? 'Organization Size' : 'Team Size'}
            value={
              <span className={styles.kvBoxValue}>
                <p>{summary.orgSize}</p>
                {orgSnapshot.skipped.users.length > 0 && (
                  <button
                    onClick={() =>
                      setSelectedSkip({
                        users: orgSnapshot.skipped.users,
                      })
                    }
                  >
                    <Warning color="#df2020" size={14} weight="fill" />
                  </button>
                )}
              </span>
            }
          />
          <KvBox
            className={kvBoxStyles.bottomBorderOnly}
            Key="Total Cost"
            value={
              <span className={styles.kvBoxValue}>
                <p>{$(summary.totalCost ?? 0)}</p>
                {hasInferredSalaries && (
                  <button
                    onClick={() =>
                      setSelectedSkip({
                        inferredSalaries: usersWithInferredSalaries,
                      })
                    }
                  >
                    <Warning color="#df2020" size={14} weight="fill" />
                  </button>
                )}
              </span>
            }
          />
          <KvBox
            className={kvBoxStyles.openBox}
            Key="Roles"
            value={
              <span className={styles.kvBoxValue}>
                <p>{summary.numRoles}</p>
                {orgSnapshot.skipped.positions.length > 0 && (
                  <button
                    onClick={() =>
                      setSelectedSkip({
                        positions: orgSnapshot.skipped.positions,
                      })
                    }
                  >
                    <Warning color="#df2020" size={14} weight="fill" />
                  </button>
                )}
              </span>
            }
          />
          <KvBox
            className={kvBoxStyles.openBox}
            Key="Avg Salary"
            value={
              <span className={styles.kvBoxValue}>
                <p>{$(summary.avgSalary ?? 0)}</p>
                {hasInferredSalaries && (
                  <button
                    onClick={() =>
                      setSelectedSkip({
                        inferredSalaries: usersWithInferredSalaries,
                      })
                    }
                  >
                    <Warning color="#df2020" size={14} weight="fill" />
                  </button>
                )}
              </span>
            }
          />
        </div>
      </div>
      {selectedSkip != null && (
        <SkipWarning {...selectedSkip} onClose={() => setSelectedSkip(null)} />
      )}
    </>
  )
}

export const getSummaryForTeam = (
  orgSnapshot: OrgSnapshot | Analysis,
  teamId: number
): Pick<OrgSnapshot, 'orgSize' | 'avgSalary' | 'totalCost' | 'numRoles'> => {
  const teamUsers = orgSnapshot.users.filter((user) => user.team_id === teamId)
  const teamSalaries = teamUsers.map((user) => user.salary_usd)
  const team = orgSnapshot.teams.find((team) => team.id === teamId)
  const openingCounts = sum(
    team?.positions?.map((p) => p.opening_count ?? 0) ?? []
  )
  const positionCosts = sum(
    team?.positions?.map(
      (p) => (p.opening_count ?? 0) * (p.avg_salary_usd ?? 0)
    ) ?? []
  )

  const totalCost = sum(teamSalaries) + positionCosts
  const totalHeadcount = teamUsers.length + openingCounts

  return {
    orgSize: totalHeadcount,
    avgSalary: totalHeadcount > 0 ? totalCost / totalHeadcount : 0,
    totalCost,
    numRoles: team?.positions.length ?? 0,
  }
}
